export interface RestoreMap {
  [placeholder: string]: string  // e.g. "[NAME]" -> "Jordan Williams"
}

// Build a restore map by diffing original text against redacted text
// Finds placeholder tokens in redacted text and maps them to original values
export function buildRestoreMap(
  originalText: string,
  redactedText: string
): RestoreMap {
  const map: RestoreMap = {}
  const placeholderRE = /\[([A-Z ]+)\]/g
  const matches = [...redactedText.matchAll(placeholderRE)]

  // Split both texts into tokens for alignment
  const originalTokens = originalText.split(/(\s+)/)
  const redactedTokens = redactedText.split(/(\s+)/)

  let origIdx = 0
  for (let i = 0; i < redactedTokens.length; i++) {
    const token = redactedTokens[i]
    if (/^\[[A-Z ]+\]$/.test(token)) {
      // This is a placeholder — capture corresponding original token(s)
      const original = originalTokens[origIdx] ?? ''
      if (original && !map[token]) {
        map[token] = original
      }
    }
    origIdx++
  }

  return map
}

// Re-insert PII from restore map into rewritten text
export function restorePII(rewrittenText: string, map: RestoreMap): string {
  let restored = rewrittenText
  for (const [placeholder, original] of Object.entries(map)) {
    // Replace all instances of placeholder with original value
    const escaped = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    restored = restored.replace(new RegExp(escaped, 'g'), original)
  }
  return restored
}

// Generate .docx-compatible blob from plain text
// Uses simple RTF-wrapped text for Word compatibility
export function textToDocxBlob(text: string): Blob {
  const rtf = `{\\rtf1\\ansi\\deff0
{\\fonttbl{\\f0 Times New Roman;}}
\\f0\\fs24
${text
  .split('\n')
  .map(line => line.trim() === '' ? '\\par' : line.replace(/[\\{}]/g, m => '\\' + m) + '\\par')
  .join('\n')}
}`
  return new Blob([rtf], { type: 'application/msword' })
}
